import { useAtomValue } from "jotai";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCustomTemplates } from "../../pages/create-content/use-custom-templates";
import { useToken } from "../../server/auth/use-token";
import type { UseSupabaseLoaderState } from "../../server/supabase/hooks/types";
import type { UUID } from "../../utils/uuid";
import { useCreateAssetCallback } from "../assets/hooks/use-upsert-asset";
import type { BrandKitStyle } from "../brand-kit/server/brand-kit-service";
import type { BrandTone } from "../brand-kit/server/brand-tone-service";
import { brandInfoAtom, brandingAtom } from "../brand-kit/store";
import type { BrandInfoSelected } from "../brand-kit/types";
import type { TemplateCustomizationObject } from "../design-huddle/brand-info-customizations/types";
import { useCreateDesignHuddleProject } from "../design-huddle/use-create-design-huddle-content-project";
import { useDesignHuddle } from "../design-huddle/use-design-huddle";
import { useGetDesignHuddleAccessToken } from "../design-huddle/use-design-huddle-access-token";
import { getRenderUrlImageEngine } from "../edit-content/hooks/use-save-asset-callback";
import type { AccountContext } from "../generic/hooks/use-account-context";
import { containsImageStyle } from "../multiformat/contains-image-style";
import type { Multiformat } from "../multiformat/store/types";
import { linkDigitalAssetToContentGenerationResult } from "../content-generation/track-content-generation";
import { contentGenerationIdAtom } from "../multiformat/store/content-generation-id-atom";
import { useGetCommsImages } from "./use-get-comms-images";
import { getToneText } from "./use-text-loader";

const toneSummaries = {
  professional: `The main content of the message is in a professional yet light hearted writing style of max 150 words. Never escape new lines.`,
  casual: `The main content of the message is in a professional yet light hearted writing style of max 100 words using emojis where appropriate. Never escape new lines.`,
  direct: `The main content of the message is in a professional, direct writing style of max 50 words. Never escape new lines.`,
};

type TemplateTextLoaderState = UseSupabaseLoaderState<{
  text: string;
  contentGenerationId: UUID | null;
  contentGenerationResultId: UUID | null;
}>;

export type CommsImage = {
  url: string;
  templateCode: string;
  pageNumber: number;
  customizations: {
    classes: TemplateCustomizationObject["classes"];
  };
  title: string;
  subjectLine: string;
  imageType: "square" | "banner";
  contentGenerationResultId?: UUID | null;
};
export type CommsImageLoaderState = UseSupabaseLoaderState<CommsImage>;

type GenerateCommsContext = {
  format: string;
  description: string;
  numImages: number;
  text: {
    get: (key: string) => TemplateTextLoaderState | undefined;
    set: (key: string, value: TemplateTextLoaderState) => void;
  };
  tones: {
    customTones: BrandTone[];
    selectedTone: BrandTone | undefined | null;
    setSelectedTone: (id: UUID | undefined) => Promise<void>;
  };
  previewImages: {
    images: (CommsImageLoaderState | undefined)[];
    selectedImage: number;
    setSelectedImage: (index: number) => void;
  };
  branding: {
    selectedStyle: BrandInfoSelected | null;
    setBrandingStyle: (style: BrandKitStyle) => void;
    selectedImageType: "square" | "banner";
    setBrandingImageType: (type: "square" | "banner") => void;
  };
  saveImage: () => Promise<void>;
};

const GenerateCommsContext = createContext<GenerateCommsContext | undefined>(undefined);

export const useGenerateCommsContext = (): GenerateCommsContext => {
  const ctx = useContext(GenerateCommsContext);
  if (!ctx) throw new Error("useCommsTemplateCache must be used within CacheProvider");
  return ctx;
};

export const GenerateCommsContextProvider: React.FC<{
  format: string;
  description: string;
  defaultBrandTone: UUID;
  children: React.ReactNode;
  customBrandTones: BrandTone[];
  multiformat: Multiformat;
  context: AccountContext;
  uploadedFileId?: string;
  db?: string;
  imageStyles?: string[];
}> = ({
  children,
  format,
  description,
  defaultBrandTone: defaultBrandToneId,
  customBrandTones,
  multiformat,
  context,
  uploadedFileId,
  db,
  imageStyles,
}) => {
  const controllerRef = useRef<AbortController | null>(null);
  const isCommunication = format === "Communication";

  const filteredLayouts = useMemo(() => {
    return multiformat.content_types
      .find((c) => c.name === format)
      ?.layouts?.map((l, index) =>
        containsImageStyle(l, imageStyles) ? { layout: l, index } : undefined,
      );
  }, [format, imageStyles, multiformat]);

  const numImages = filteredLayouts?.length ?? 9;

  const defaultImages = useMemo(
    () =>
      filteredLayouts?.map((x) => {
        return x?.layout ? { data: null, loading: true as const, error: null } : undefined;
      }) ?? [],
    [filteredLayouts],
  );
  const { data: customTemplates, loading: customTemplatesLoading } =
    useCustomTemplates(multiformat);
  const contentGenerationId = useAtomValue(contentGenerationIdAtom);
  const getImages = useGetCommsImages(
    format,
    description,
    context?.industry ?? undefined,
    uploadedFileId,
    db,
    filteredLayouts,
    contentGenerationId,
  );

  //TODO: create a new Branding Bar with no jotai dependency
  const brandingBar = useAtomValue(brandingAtom);
  const brandInfo = useAtomValue(brandInfoAtom);
  const [brandingStyle, setBrandingStyle] = useState<BrandInfoSelected | null>(null);
  const [brandingImageType, setBrandingImageType] = useState<"square" | "banner">("square");
  const getAccessToken = useGetDesignHuddleAccessToken();
  const { loading: designHuddleLoading } = useDesignHuddle([]);
  const createDesignHuddleProject = useCreateDesignHuddleProject();
  const createAsset = useCreateAssetCallback();

  const [selectedImage, setSelectedImage] = useState(
    defaultImages.indexOf(defaultImages.find((x) => x !== undefined)) ?? 0,
  );

  const [textCache, setTextCache] = useState<Map<string, TemplateTextLoaderState> | null>(null);
  const [images, setImages] = useState<(CommsImageLoaderState | undefined)[]>(defaultImages);

  const defaultBrandTone = useMemo(
    () => customBrandTones.find((b) => b.id === defaultBrandToneId),
    [defaultBrandToneId, customBrandTones],
  );
  const [selectedBrandTone, setSelectedBrandTone] = useState<BrandTone | undefined>(
    defaultBrandTone,
  );
  const getToken = useToken();

  const get = useCallback(
    (key: string): TemplateTextLoaderState | undefined => textCache?.get(key),
    [textCache],
  );

  const set = useCallback((key: string, value: TemplateTextLoaderState): void => {
    setTextCache((prev) => {
      const newCache = new Map(prev);
      newCache.set(key, value);
      return newCache;
    });
  }, []);

  const setBrandToneText = useCallback(
    async (tone: "custom", summary: string, salutation: string | null, signOff: string | null) => {
      const key = summary;
      try {
        const text =
          textCache?.get(key) ??
          (await getToneText(
            tone,
            summary,
            {
              format,
              description,
              fileId: uploadedFileId,
              salutation: isCommunication && salutation ? salutation : undefined,
              signOff: isCommunication && signOff ? signOff : undefined,
            },
            await getToken(),
            contentGenerationId,
          ));
        text &&
          set(key, {
            loading: false,
            ...text,
          });
      } catch (err) {
        set(key, {
          data: null,
          loading: false,
          error: new Error(err as string),
        });
      }
    },
    [
      textCache,
      format,
      description,
      uploadedFileId,
      isCommunication,
      getToken,
      set,
      contentGenerationId,
    ],
  );

  const handleCustomBrandToneChange = useCallback(
    async (id: UUID | undefined) => {
      const brandTone = customBrandTones.find((b) => b.id === id);
      if (!brandTone) return;
      setSelectedBrandTone(brandTone);

      await setBrandToneText("custom", brandTone.summary, brandTone.salutation, brandTone.sign_off);
    },
    [customBrandTones, setBrandToneText],
  );

  const handleImageLoaded = useCallback((imgNum: number, imageLoader: CommsImageLoaderState) => {
    setImages((prevImages) => {
      const newImages = [...prevImages];
      newImages[imgNum] = {
        ...newImages[imgNum],
        ...imageLoader,
      };
      return newImages;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!images[selectedImage]?.data?.templateCode || designHuddleLoading) {
      throw new Error("Unable to save image, please try again later");
    }

    const projectId = await createDesignHuddleProject({
      templateCode: images[selectedImage].data.templateCode,
      customizations: images[selectedImage].data.customizations,
      pageNumber: selectedImage + 1,
    });

    const png = await getRenderUrlImageEngine({
      getToken,
      accessToken: await getAccessToken([projectId]),
      projectId,
      format: "png",
      filename: projectId,
    });

    if (!png.success) {
      throw new Error("Failed to save image");
    }

    const title = `${images[0]?.data?.title ?? format} ${new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14)}`;
    const result = await createAsset({
      projectId,
      contentType: format,
      thumbnailUrl: png.url,
      publicUrl: png.url,
      title,
    });

    const contentGenerationResultId = images[selectedImage]?.data?.contentGenerationResultId;
    if (result.success && contentGenerationResultId) {
      void linkDigitalAssetToContentGenerationResult(
        await getToken(),
        contentGenerationResultId,
        result.asset.id,
      );
    }
  }, [
    createAsset,
    createDesignHuddleProject,
    designHuddleLoading,
    format,
    getAccessToken,
    getToken,
    images,
    selectedImage,
  ]);

  useEffect(() => {
    if (!brandingStyle) return;

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImages(defaultImages);

    void getImages({
      branding: brandingStyle,
      brandingImageType,
      brandingBar,
      brandInfo,
      onImageLoaded: handleImageLoaded,
      signal: controller.signal,
    }).catch((err: Error) => {
      if (err?.name === "AbortError") return;
      console.log(err);
    });
  }, [
    getImages,
    brandingStyle,
    brandingImageType,
    multiformat,
    defaultImages,
    brandingBar,
    brandInfo,
    numImages,
    handleImageLoaded,
  ]);

  useEffect(() => {
    if (customTemplatesLoading) return;

    const squares = customTemplates?.["Square Comms"] ?? [];
    const banners = customTemplates?.["Landscape Comms"] ?? [];

    if (isCommunication) {
      const defaultStyle =
        squares.length > 0
          ? squares[0]!.template.template_code
          : banners[0]?.template.template_code;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBrandingStyle({
        branding_type: defaultStyle
          ? { type: "custom", value: defaultStyle }
          : { type: "standard", value: "bold" },
        default_tone: "professional",
        initialized: true,
      });
    } else {
      const custom =
        (customTemplates?.[format]?.length ?? 0 > 0) ? customTemplates![format]![0] : null;

      setBrandingStyle({
        branding_type:
          custom && custom.template.template_code
            ? { type: "custom", value: custom.template.template_code }
            : { type: "standard", value: "bold" },
        default_tone: "professional",
        initialized: true,
      });
    }
  }, [setBrandingStyle, customTemplatesLoading, customTemplates, format, isCommunication]);

  const handleSetBrandingStyle = useCallback((style: BrandKitStyle) => {
    setBrandingStyle((prev) => ({
      branding_type: style,
      default_tone: prev?.default_tone || "professional",
      initialized: true,
    }));
  }, []);

  useEffect(() => {
    if (textCache) return;

    (async () => {
      const token = await getToken();
      const config = { format, description, fileId: uploadedFileId, context };
      const customConfig = {
        format,
        description,
        fileId: uploadedFileId,
        salutation: isCommunication ? (defaultBrandTone?.salutation ?? undefined) : undefined,
        signOff: isCommunication ? (defaultBrandTone?.sign_off ?? undefined) : undefined,
        context,
      };
      const [professional, casual, direct, custom] = await Promise.all([
        getToneText(
          "professional",
          toneSummaries["professional"],
          config,
          token,
          contentGenerationId,
        ),
        getToneText("casual", toneSummaries["casual"], config, token, contentGenerationId),
        getToneText("direct", toneSummaries["direct"], config, token, contentGenerationId),
        defaultBrandTone
          ? getToneText(
              "custom",
              defaultBrandTone.summary,
              customConfig,
              token,
              contentGenerationId,
            )
          : null,
      ]);

      setTextCache((prev) => {
        const map = new Map(prev);
        professional && map.set("professional", { ...professional, loading: false });
        casual && map.set("casual", { ...casual, loading: false });
        direct && map.set("direct", { ...direct, loading: false });
        defaultBrandTone &&
          custom &&
          map.set(defaultBrandTone.summary, { ...custom, loading: false });
        return map;
      });
    })().catch(console.error);
  }, [
    setBrandToneText,
    defaultBrandTone,
    getToken,
    textCache,
    description,
    format,
    uploadedFileId,
    isCommunication,
    context,
    contentGenerationId,
  ]);

  return (
    <GenerateCommsContext.Provider
      value={{
        format,
        description,
        numImages,
        text: { get, set },
        tones: {
          customTones: customBrandTones,
          selectedTone: selectedBrandTone,
          setSelectedTone: handleCustomBrandToneChange,
        },
        previewImages: {
          images,
          selectedImage,
          setSelectedImage,
        },
        branding: {
          selectedStyle: brandingStyle,
          setBrandingStyle: handleSetBrandingStyle,
          selectedImageType: brandingImageType,
          setBrandingImageType,
        },
        saveImage: handleSave,
      }}
    >
      {children}
    </GenerateCommsContext.Provider>
  );
};
